import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PdfViewerComponent } from 'ng2-pdf-viewer';
import { Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { AttachmentService } from 'src/app/core/services/attachment';
import { ContentDocumentLink } from 'src/app/models/content-document-link.model';
import { CreateTicketComponent } from 'src/app/tickets/containers/create-ticket/create-ticket.component';

@Component({
  selector: 'app-billing-detail',
  templateUrl: './billing-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BillingDetailComponent implements OnChanges, OnDestroy {
  @Input() selectedInvoice: ContentDocumentLink;

  @Input() isInvoicesLoading: boolean;

  @Output() downloadInvoice: EventEmitter<any> = new EventEmitter();

  @Input() browserSize: string;

  private destroy$ = new Subject<void>();

  subscriptions: any[];

  fileSubscription: Subscription;

  pdfLoading = false;

  pdfResourceUrl: SafeResourceUrl | null = null;

  pdfUrl: any;

  canEmbedPdf: boolean;

  totalPages: number;

  page = 1;

  isLoaded = false;

  @ViewChild(PdfViewerComponent, { static: true })
  pdfComponent: PdfViewerComponent;

  constructor(
    private attachmentService: AttachmentService,
    private domSanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.canEmbedPdf = true;
    this.isLoaded = false;
    this.pdfLoading = false;
  }

  ngOnChanges() {
    if (this.selectedInvoice) {
      this.pdfLoading = true;
      this.canEmbedPdf = true;
      this.isLoaded = false;
      this.viewEmbeddedPDF(this.selectedInvoice);
    } else {
      this.pdfLoading = false;
      this.pdfUrl = null;
      this.pdfResourceUrl = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDownload(invoice: ContentDocumentLink): void {
    this.downloadInvoice.emit(invoice);
  }

  viewEmbeddedPDF(invoice: ContentDocumentLink): void {
    if (invoice) {
      this.page = 1;
      this.pdfResourceUrl = null;
      this.pdfLoading = true;
      this.canEmbedPdf = true;

      if (this.fileSubscription) {
        this.fileSubscription.unsubscribe();
      }

      this.fileSubscription = this.attachmentService
        .getInvoiceBlob(invoice)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (pdf) => {
            this.pdfLoading = false;
            this.pdfResourceUrl =
              this.domSanitizer.bypassSecurityTrustResourceUrl(pdf.url);
            this.pdfUrl = pdf.url;
            this.canEmbedPdf = true;
            this.changeDetectorRef.detectChanges();
          },
          error: (error) => {
            console.error('Error loading PDF:', error);
            this.pdfLoading = false;
            this.canEmbedPdf = false;
            this.onError();
          },
        });
    }
  }

  afterLoadComplete(pdfData: any): void {
    this.totalPages = pdfData.numPages;
    this.isLoaded = true;
    this.pdfLoading = false;
    this.canEmbedPdf = true;
    this.changeDetectorRef.detectChanges();
  }

  incrementPage(amount: number): void {
    this.page += amount;
  }

  onError(): void {
    this.pdfLoading = false;
    this.canEmbedPdf = false;
    this.isLoaded = false;
    console.error('PDF viewer error occurred');
    this.changeDetectorRef.detectChanges();
  }

  openCreateTicket(): void {
    const browserSize = window.innerWidth <= 700 ? 'small' : '';
    const dialogConfig: MatDialogConfig = {
      autoFocus: true,
      minHeight: browserSize === 'small' ? '315px' : '380px',
      width: browserSize === 'small' ? 'calc(100vw - 20px)' : '650px',
      maxWidth: '650px',
      data: {
        browserSize,
        originPage: 'Billing',
      },
      disableClose: true,
    };

    this.dialog.open(CreateTicketComponent, dialogConfig);
  }
}
